import { useEffect, useRef, useState } from 'react'
import type { ShapeData } from '@/lib/shapes/types/shape-data'
import type { ShapePhotoFill } from '@/lib/photos/types/shape-photo-fill'
import type { CropFrameGeom } from '@/lib/photos/crop-frame-resize'
import type { CropDrag } from '../types/crop-drag'

/**
 * The overlay's live drag state: the active gesture plus local geometry/photoFill copies that
 * follow the `shape` prop whenever no drag is in progress.
 */
export function useCropDragState(shape: ShapeData) {
  const svgRef = useRef<SVGSVGElement>(null)
  const dragRef = useRef<CropDrag | null>(null)
  const [, forceUpdate] = useState(0)

  // Tracks live geometry/photoFill locally so the overlay can react synchronously during a
  // drag without waiting for the prop to round-trip through React state.
  const geomRef = useRef<CropFrameGeom>({
    x: shape.x,
    y: shape.y,
    width: shape.width,
    height: shape.height,
  })
  const photoFillRef = useRef<ShapePhotoFill | undefined>(shape.photoFill)

  if (!dragRef.current) {
    geomRef.current = { x: shape.x, y: shape.y, width: shape.width, height: shape.height }
    photoFillRef.current = shape.photoFill
  }

  // Release any in-progress drag if the overlay unmounts mid-gesture (e.g. Escape exits crop
  // mode while a handle is held down).
  useEffect(() => {
    return () => {
      dragRef.current = null
    }
  }, [])

  return { svgRef, dragRef, forceUpdate, geomRef, photoFillRef }
}

export type CropDragState = ReturnType<typeof useCropDragState>
